import { computed, inject, Injectable, signal, Signal } from '@angular/core';

import { FeatureFlagName } from './feature-flags';
import { UNLEASH_CLIENT } from './unleash-client';

@Injectable({ providedIn: 'root' })
export class FeatureFlagService {
    private readonly client = inject(UNLEASH_CLIENT);
    private readonly enabledFlags = signal<ReadonlySet<string>>(new Set());
    private readonly flagSignals = new Map<FeatureFlagName, Signal<boolean>>();
    private readonly loaded = signal(false);
    private readonly unreachable = signal(false);

    readonly ready = this.loaded.asReadonly();

    readonly error = this.unreachable.asReadonly();

    start(): void {
        if (!this.client) {
            this.unreachable.set(true);
            return;
        }

        this.client.on('ready', () => {
            this.loaded.set(true);
            this.readToggles();
        });
        this.client.on('update', () => this.readToggles());
        this.client.on('error', () => this.unreachable.set(true));

        this.client.start().catch(() => this.unreachable.set(true));
    }

    isEnabled(flag: FeatureFlagName): boolean {
        return this.enabledFlags().has(flag);
    }

    flag(name: FeatureFlagName): Signal<boolean> {
        const existing = this.flagSignals.get(name);
        if (existing) return existing;

        const created = computed(() => this.isEnabled(name));
        this.flagSignals.set(name, created);

        return created;
    }

    private readToggles(): void {
        const toggles = this.client?.getAllToggles() ?? [];

        this.enabledFlags.set(new Set(toggles.filter(({ enabled }) => enabled).map(({ name }) => name)));
    }
}
